/**
 * Token Usage Data Store
 *
 * TTL-based cache for token usage API responses (overview, trends, users).
 * Keyed by filter parameters so different date ranges have separate entries.
 * Survives in-app navigation — only expires after TTL or explicit invalidation.
 *
 * @module store/tokenUsage/tokenUsageDataStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { TokenUsageService } from '../../features/tokenUsage/services/TokenUsageService';
import { getDefaultDateRange } from '../../features/tokenUsage/utils/dateFormatters';
import { calculateSummaryStats, transformTrendsForChart, transformUsersForTable } from '../../features/tokenUsage/utils/chartDataTransform';

const DATA_CACHE_TTL = 5 * 60_000; // 5 minutes (matches tokenUsageCachedAxios TTL)
const MAX_CACHE_ENTRIES = 20;

// Stable default references — inline [] creates a new object every render,
// which breaks useShallow's shallow comparison and causes infinite re-renders.
const EMPTY_ARRAY = [];

// ── Cache key builders ──────────────────────────────────────

const buildOverviewKey = (orgId, startDate, endDate) =>
  `overview::${orgId}::${startDate}::${endDate}`;

const buildTrendsKey = (orgId, startDate, endDate, groupBy, userId) =>
  `trends::${orgId}::${startDate}::${endDate}::${groupBy}::${userId || 'all'}`;

const buildUsersKey = (orgId, startDate, endDate, sortBy, limit) =>
  `users::${orgId}::${startDate}::${endDate}::${sortBy}::${limit || 'all'}`;

// ── Non-reactive in-flight tracking ─────────────────────────
// Kept outside Zustand state to avoid triggering re-renders on loading
// state changes, which would cascade across multiple subscriber hooks.

const activeFetchesMap = {};

// ── Eviction helper ─────────────────────────────────────────

const evictOldest = (cache) => {
  const keys = Object.keys(cache);
  if (keys.length <= MAX_CACHE_ENTRIES) return cache;

  let oldestKey = keys[0];
  let oldestTime = cache[keys[0]].timestamp;
  for (let i = 1; i < keys.length; i++) {
    if (cache[keys[i]].timestamp < oldestTime) {
      oldestKey = keys[i];
      oldestTime = cache[keys[i]].timestamp;
    }
  }

  const next = { ...cache };
  delete next[oldestKey];
  logger.debug('Evicting oldest token usage cache entry', { key: oldestKey });
  return next;
};

// ── Store ───────────────────────────────────────────────────

export const useTokenUsageDataStore = create((set, get) => ({
  // { [cacheKey]: { data: any, error: string|null, timestamp: number } }
  cache: {},

  /**
   * Return cached entry if still fresh, null otherwise.
   */
  getCachedData: (key) => {
    const cached = get().cache[key];
    if (!cached) return null;
    if (Date.now() - cached.timestamp > DATA_CACHE_TTL) return null;
    return cached;
  },

  /**
   * Store data in cache with automatic LRU eviction.
   */
  setCachedData: (key, data, error = null) => {
    set(state => {
      let newCache = { ...state.cache };
      newCache[key] = { data, error, timestamp: Date.now() };
      newCache = evictOldest(newCache);
      return { cache: newCache };
    });
  },

  // ── Async load actions ──────────────────────────────────

  /**
   * Load overview data with TTL caching.
   * @returns {Promise<{success: boolean, data?: any, cached?: boolean, error?: string}>}
   */
  loadOverview: async (userData, startDate, endDate, force = false) => {
    if (!userData?.organizationId) {
      return { success: false, error: 'User data required' };
    }

    const dateRange = (startDate && endDate)
      ? { startDate, endDate }
      : getDefaultDateRange();
    const key = buildOverviewKey(userData.organizationId, dateRange.startDate, dateRange.endDate);

    // Cache hit
    if (!force) {
      const cached = get().getCachedData(key);
      if (cached && !cached.error) {
        logger.debug('Using cached overview data', { key });
        return { success: true, data: cached.data, cached: true };
      }
    }

    // Prevent duplicate in-flight requests
    if (activeFetchesMap[key] && !force) {
      return { success: false, error: 'Already fetching' };
    }

    activeFetchesMap[key] = true;

    try {
      logger.info('Loading overview from API', { startDate: dateRange.startDate, endDate: dateRange.endDate });
      const rawData = await TokenUsageService.getOverview(userData, dateRange.startDate, dateRange.endDate);

      if (rawData) {
        const processedData = calculateSummaryStats(rawData);
        get().setCachedData(key, processedData);
        return { success: true, data: processedData, cached: false };
      } else {
        const error = 'No data received from server';
        get().setCachedData(key, null, error);
        return { success: false, error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch overview';
      logger.error('Error loading overview', { error: errorMsg });
      get().setCachedData(key, null, errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      delete activeFetchesMap[key];
    }
  },

  /**
   * Load trends data with TTL caching.
   */
  loadTrends: async (userData, startDate, endDate, groupBy = 'day', userId = null, force = false) => {
    if (!userData?.organizationId) {
      return { success: false, error: 'User data required' };
    }

    const dateRange = (startDate && endDate)
      ? { startDate, endDate }
      : getDefaultDateRange();
    const key = buildTrendsKey(userData.organizationId, dateRange.startDate, dateRange.endDate, groupBy, userId);

    if (!force) {
      const cached = get().getCachedData(key);
      if (cached && !cached.error) {
        logger.debug('Using cached trends data', { key });
        return { success: true, data: cached.data, cached: true };
      }
    }

    if (activeFetchesMap[key] && !force) {
      return { success: false, error: 'Already fetching' };
    }

    activeFetchesMap[key] = true;

    try {
      logger.info('Loading trends from API', { startDate: dateRange.startDate, endDate: dateRange.endDate, groupBy });
      const rawData = await TokenUsageService.getTrends(userData, dateRange.startDate, dateRange.endDate, groupBy, userId);

      if (Array.isArray(rawData)) {
        const transformedData = transformTrendsForChart(rawData);
        get().setCachedData(key, transformedData);
        return { success: true, data: transformedData, cached: false };
      } else {
        const error = 'Invalid data format received';
        get().setCachedData(key, null, error);
        return { success: false, error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch trends';
      logger.error('Error loading trends', { error: errorMsg });
      get().setCachedData(key, null, errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      delete activeFetchesMap[key];
    }
  },

  /**
   * Load users data with TTL caching.
   */
  loadUsers: async (userData, startDate, endDate, sortBy = 'cost', limit = null, force = false) => {
    if (!userData?.organizationId) {
      return { success: false, error: 'User data required' };
    }

    const dateRange = (startDate && endDate)
      ? { startDate, endDate }
      : getDefaultDateRange();
    const key = buildUsersKey(userData.organizationId, dateRange.startDate, dateRange.endDate, sortBy, limit);

    if (!force) {
      const cached = get().getCachedData(key);
      if (cached && !cached.error) {
        logger.debug('Using cached users data', { key });
        return { success: true, data: cached.data, cached: true };
      }
    }

    if (activeFetchesMap[key] && !force) {
      return { success: false, error: 'Already fetching' };
    }

    activeFetchesMap[key] = true;

    try {
      logger.info('Loading users from API', { startDate: dateRange.startDate, endDate: dateRange.endDate, sortBy });
      const rawData = await TokenUsageService.getUsers(userData, dateRange.startDate, dateRange.endDate, sortBy, limit);

      if (Array.isArray(rawData)) {
        const transformedData = transformUsersForTable(rawData);
        get().setCachedData(key, transformedData);
        return { success: true, data: transformedData, cached: false };
      } else {
        const error = 'Invalid data format received';
        get().setCachedData(key, null, error);
        return { success: false, error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch users';
      logger.error('Error loading users', { error: errorMsg });
      get().setCachedData(key, null, errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      delete activeFetchesMap[key];
    }
  },

  // ── Invalidation ────────────────────────────────────────

  /**
   * Clear all cached data and in-flight tracking.
   */
  invalidateAll: () => {
    logger.debug('Invalidating all token usage data cache');
    Object.keys(activeFetchesMap).forEach(k => delete activeFetchesMap[k]);
    set({ cache: {} });
  },

  /**
   * Clear cached entries matching a prefix.
   */
  invalidateByPrefix: (prefix) => {
    logger.debug('Invalidating token usage cache by prefix', { prefix });
    set(state => {
      const newCache = {};
      Object.entries(state.cache).forEach(([k, v]) => {
        if (!k.startsWith(prefix)) {
          newCache[k] = v;
        }
      });
      return { cache: newCache };
    });
  }
}));

// ── Exported key builders (for selectors) ───────────────────

export { buildOverviewKey, buildTrendsKey, buildUsersKey };
export { DATA_CACHE_TTL };

// ── Parameterized selector hooks ────────────────────────────

export const useOverviewData = (orgId, startDate, endDate) =>
  useTokenUsageDataStore(
    useShallow(state => {
      const key = buildOverviewKey(orgId, startDate, endDate);
      const cached = state.cache[key];
      return {
        data: cached?.data ?? null,
        error: cached?.error ?? null
      };
    })
  );

export const useTrendsData = (orgId, startDate, endDate, groupBy, userId) =>
  useTokenUsageDataStore(
    useShallow(state => {
      const key = buildTrendsKey(orgId, startDate, endDate, groupBy, userId);
      const cached = state.cache[key];
      return {
        data: cached?.data ?? EMPTY_ARRAY,
        error: cached?.error ?? null
      };
    })
  );

export const useUsersData = (orgId, startDate, endDate, sortBy, limit) =>
  useTokenUsageDataStore(
    useShallow(state => {
      const key = buildUsersKey(orgId, startDate, endDate, sortBy, limit);
      const cached = state.cache[key];
      return {
        data: cached?.data ?? EMPTY_ARRAY,
        error: cached?.error ?? null
      };
    })
  );

// ── Actions hook ────────────────────────────────────────────

export const useTokenUsageDataActions = () =>
  useTokenUsageDataStore(
    useShallow(state => ({
      loadOverview: state.loadOverview,
      loadTrends: state.loadTrends,
      loadUsers: state.loadUsers,
      invalidateAll: state.invalidateAll,
      invalidateByPrefix: state.invalidateByPrefix
    }))
  );
